import Base from '@domain/composition/Base';
import { UnitConverter } from '@vibialighting/vibia-utils'
import { CATALOG, PRODUCT_SUBCATEGORIES, CONFIGURATOR_TYPES, composingPendantSubFamilyCodes } from '../data/Constants'

/**
 * A pendant length in metres → the centimetres that represent it for its catalog (DV-6315).
 *
 * The metric catalogs are manufactured to the centimetre, so the height is rounded UP to the next
 * whole one — the historical behaviour, unchanged.
 *
 * The US catalog is manufactured in 3/8 in steps, and 3/8 in is 0.9525 cm: NO whole centimetre can
 * represent that grid. Rounding there moved the length by up to a whole step on the way back to
 * inches — a height picked as 62 1/4" came back as 62 5/8" in the article, the BOM and every
 * document built from them. So the US catalog keeps the exact centimetres; the UI already
 * guarantees they sit on the grid. `toDecimalPlaces` only trims the float residue of the
 * inches→metres conversion (158.11500000063248 → 158.115), it is not a rounding rule.
 */
export const pendantHeightToCm = (meters: number, catalogId: number): number =>
    catalogId === CATALOG.USA ? UnitConverter.toDecimalPlaces(meters * 100, 4) : Math.ceil(meters * 100)

export const instanceofType = <T extends Base = Base>(element: T, typeName: string) => {
    let currentClass = element.constructor as typeof Base;

    while (currentClass) {
        if (currentClass.className === typeName) {
            return true;
        }
        const superClass = Object.getPrototypeOf(currentClass.prototype);
        if (!superClass) break;
        currentClass = superClass.constructor as typeof Base;
    }
    return false;
}



export const isModelComposingPendants = (model: string) => {
    const cpFamilies = Object.keys(composingPendantSubFamilyCodes)
    const result = cpFamilies.find(family => {
        const cpModels = composingPendantSubFamilyCodes[family]
        return cpModels.find(cpModel => cpModel == model)
    })
    return Boolean(result)
}

const allowedTypes = ['days', 'weeks', 'months'] as const
type AllowedUnits = typeof allowedTypes[number]
export const convertTimeTo = (date: number, unit: AllowedUnits): number => {

    if (typeof date !== 'number' || !allowedTypes.includes(unit)) {
        throw new Error('Invalid input');
    }

    // Milisegundos en una semana (7 días * 24 horas * 60 minutos * 60 segundos * 1000 milisegundos)
    const millisecondsInADay = 24 * 60 * 60 * 1000;
    const millisecondsInAWeek = 7 * millisecondsInADay;
    const millisecondsInAMonth = 30 * millisecondsInADay; // Aproximadamente 30 días por mes

    switch (unit) {
        case 'days':
            return date / millisecondsInADay;
        case 'weeks':
            return date / millisecondsInAWeek;
        case 'months':
            return date / millisecondsInAMonth;
        default:
            return NaN;
    }
}

export function protectFromUndefinedOrNaN(value, fallback) {
    if (!value || isNaN(value)) {
        return fallback
    } else {
        return value
    }
}


export const isComposingPendantsCategory = (category) => category?.subcategory?.id === PRODUCT_SUBCATEGORIES.COMPOSING_PENDANTS
export const isCreaConfigurator = (configurator) => configurator?.id === CONFIGURATOR_TYPES.CREA
export const isDrawingConfigurator = (configurator) => configurator?.id === CONFIGURATOR_TYPES.DRAWING
export const isNoDrawingConfigurator = (configurator) => configurator?.id === CONFIGURATOR_TYPES.NODRAWING